import React, { useState, useEffect, createContext, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
  linkWithCredential,
} from 'firebase/auth';
import { auth, googleProvider } from '../../firebase';
import { getInstallUrl } from '../../services/githubAppApi';
import { ROLES, PERMISSIONS, determineUserRole, getUserClaims } from './constants';
import { analytics, AnalyticsEvents } from '../../services/analytics';

// Authentication Context
export const AuthContext = createContext();

// Authentication Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const role = await determineUserRole(firebaseUser);
        const claims = await getUserClaims(firebaseUser);
        const name = firebaseUser.displayName || firebaseUser.email;

        const userSession = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: name,
          role: role,
          permissions: PERMISSIONS[role],
          photoURL: firebaseUser.photoURL,
          githubUsername: claims.githubUsername,
          canManageOrgs: claims.canManageOrgs,
        };
        setUser(userSession);
        analytics.identify(firebaseUser.uid, { role });
      } else {
        setUser(null);
        analytics.reset();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      const role = await determineUserRole(firebaseUser);
      const claims = await getUserClaims(firebaseUser);
      const userSession = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email,
          role: role,
          permissions: PERMISSIONS[role],
          photoURL: firebaseUser.photoURL,
          githubUsername: claims.githubUsername,
          canManageOrgs: claims.canManageOrgs,
      };

      return { success: true, user: userSession };
    } catch (error) {
      let errorMessage = 'An unexpected error occurred. Please try again.';
      switch (error.code) {
        case 'auth/invalid-email':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password.';
          break;
        default:
          console.error('Firebase login error:', error);
          break;
      }
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Handle OAuth login (Google only).
   */
  const handleOAuthLogin = async (provider, providerName) => {
    try {
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;

      const role = await determineUserRole(firebaseUser);
      const claims = await getUserClaims(firebaseUser);
      const userSession = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email,
        role: role,
        permissions: PERMISSIONS[role],
        provider: providerName,
        photoURL: firebaseUser.photoURL,
        githubUsername: claims.githubUsername,
        canManageOrgs: claims.canManageOrgs,
      };

      return { success: true, user: userSession };
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        const email = error.customData?.email;
        if (email) {
          try {
            const methods = await fetchSignInMethodsForEmail(auth, email);

            let existingMethod = 'another method';
            if (methods.includes('google.com')) {
              existingMethod = 'Google';
            } else if (methods.includes('password')) {
              existingMethod = 'email/password';
            }

            return {
              success: false,
              error: `This email is already registered with ${existingMethod}. Please sign in with ${existingMethod} first.`,
              pendingCredential: error.credential,
              email: email,
              existingMethods: methods
            };
          } catch (fetchError) {
            console.error('Error fetching sign-in methods:', fetchError);
          }
        }
        return {
          success: false,
          error: 'An account already exists with this email using a different sign-in method.'
        };
      }

      let errorMessage = 'An unexpected error occurred. Please try again.';
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in popup was closed before completing.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Sign-in popup was blocked by the browser. Please allow popups.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Another popup is already open.';
          break;
        default:
          console.error(`${providerName} login error:`, error);
          break;
      }
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async () => {
    return handleOAuthLogin(googleProvider, 'Google');
  };

  /**
   * Link a pending credential to the current user's account.
   */
  const linkPendingCredential = async (pendingCredential) => {
    try {
      if (auth.currentUser && pendingCredential) {
        await linkWithCredential(auth.currentUser, pendingCredential);
        return { success: true, message: 'Account linked successfully!' };
      }
      return { success: false, error: 'No pending credential to link.' };
    } catch (error) {
      console.error('Error linking credential:', error);
      return { success: false, error: 'Failed to link account. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  /**
   * Navigate to GitHub App installation flow.
   * Replaces the old OAuth-based linkGitHub.
   */
  const connectGitHub = () => {
    const url = getInstallUrl();
    window.location.href = url;
  };

  /**
   * Get the list of linked providers for the current user.
   */
  const getLinkedProviders = () => {
    if (!auth.currentUser) return [];
    return auth.currentUser.providerData.map(p => p.providerId);
  };

  const hasPermission = (permission) => {
    return user && user.permissions && user.permissions.includes(permission);
  };

  const isSuperadmin = () => {
    return user && user.role === ROLES.SUPERADMIN;
  };

  const isOwner = () => {
    return user && (user.role === ROLES.OWNER || user.role === ROLES.SUPERADMIN);
  };

  const isVisitor = () => {
    return user && user.role === ROLES.VISITOR;
  };

  // Alias for backward compatibility
  const isAdmin = isSuperadmin;

  /**
   * Get the Firebase ID token for API authentication.
   */
  const getIdToken = async () => {
    if (!auth.currentUser) {
      throw new Error('No user signed in');
    }
    return await auth.currentUser.getIdToken();
  };

  // Create an enhanced user object that includes getIdToken method
  const enhancedUser = user ? {
    ...user,
    getIdToken,
  } : null;

  const value = {
    user: enhancedUser,
    login,
    loginWithGoogle,
    linkPendingCredential,
    connectGitHub,
    getLinkedProviders,
    logout,
    hasPermission,
    isSuperadmin,
    isOwner,
    isAdmin,
    isVisitor,
    loading,
    getIdToken,
    ROLES,
    PERMISSIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
